import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import AddressInputWithAutocomplete from '../../../common/adress-input-with-autocomplete/address-input-with-autocomplete';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import ChecklistIcon from '@mui/icons-material/Checklist';

import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import React from 'react';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { emptyPickupTask } from '../../../../data/pickuptasks';
import { DeliveryState, Workflow } from '../../../../enums/orders';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { emptyCustomer } from '../../../../data/customers';
import { CustomerType } from '../../../../interfaces/common/customer';
import GenericTable from '../../../common/generic-table/generic-table';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import { useImmer } from 'use-immer';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import { displayLocation } from '../../../../utils/locations';
import { ILocation } from '../../../../interfaces/common/location';
import { isMobile, isTablet } from 'react-device-detect';
import { add, displayMoney, Money } from '../../../../utils/money';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import Grid2 from '@mui/material/Unstable_Grid2';
import { showDeliveryState } from '../../../common/styles/order-states.style';
import { showWorkflow } from '../../../common/styles/workflow-display';
import { emptyMerchant } from '../../../../data/merchants';
import CreateLocationForm from '../../location-form/location-form';
import CreatePickupLocationForm from '../../pickup-location-form/pickup-location-form';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import CreateMerchantForm from '../../merchants/merchant-forms/create-or-edit-merchant';
import { showCustomerAutocompleteOption } from '../../../../utils/customers';
import { compute_total_pop } from '../../../common/report-builder/utils';

interface CreatePickupRequestProps {
  onSuccess: (p: IPickupTask, is_newly_created: boolean) => void;
}

function CreatePickupRequest({ onSuccess }: CreatePickupRequestProps) {
  const [taskData, setTaskData] = useState<IPickupTask>(emptyPickupTask);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [createPickupLocation, setCreatePickupLocation] = useState(false);
  const [createLocation, setCreateLocation] = useState(false);
  const [creatingNewMerchantOpen, setCreatingNewMerchantOpen] = useState(false);

  const { user, config } = useContext(AuthContext);

  const [selectedOrders, updateSelectedOrders] = useImmer<
    Map<string, OrderType>
  >(new Map());

  const resetSelectionState = () => {
    updateSelectedOrders((selectedOrders) => selectedOrders.clear());
  };

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const totalSelected = selectedOrders.size;

  const searchOptions = ['Order_Id', 'Ref_Number'];
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: '',
    searchValue: '',
    searchOperation: 'equals',
  });

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  useEffect(() => {
    setTaskData({ ...emptyPickupTask, workflow: taskData.workflow });
  }, [taskData.workflow]);

  useEffect(() => {
    const getAndSetOrders = async () => {
      setLoadingOrders(true);
      resetSelectionState();
      resetPaginationControls();

      if (
        taskData.workflow === Workflow.STANDARD &&
        (taskData.merchant == null || taskData.merchant_location == null)
      ) {
        setOrders([]);
        setLoadingOrders(false);
        return;
      }

      if (
        taskData.workflow === Workflow.RETURN &&
        (taskData.merchant == null ||
          taskData.customer == null )
      ) {
        setOrders([]);
        setLoadingOrders(false);
        return;
      }

      const relevant_fields = [
        'id',
        'order_id',
        'reference_id',
        'creation_date',
        'customer',
        'address',
        'district',
        'merchant',
        'governorate',
        'workflow',
        'price',
        'labels',
        'pop',
        'delivery_fee',
        'number_of_packages',
        'delivery_state',
        'merchant_location',
        'customer_location',
        'payment_state',
        'branch',
        'tracking_hash',
        'public_note',
        'return_reason',
        'exchange_for',
      ];

      let endpoint = `${API_ENDPOINT}/api/v1/orders/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      if (controller.searchColumn !== '' && controller.searchValue !== '') {
        endpoint += `&${controller.searchColumn.toLowerCase()}__${controller.searchOperation}=${encodeURIComponent(controller.searchValue)}`;
      }

      endpoint += `&delivery_state=${DeliveryState.CREATED}`;
      // we should only consider orders related to a single merchant only, otherwise it won't be possible for merchants to track
      // pickup tasks of return orders since different merchants might be associated to the same task
      endpoint += `&workflow=${taskData.workflow}&merchant=${taskData.merchant?.id}`;

      if (taskData.workflow === Workflow.RETURN)
        endpoint += `&customer=${taskData.customer?.id}`;
      else endpoint += `&merchant_location=${taskData.merchant_location?.id}`;

      // const filterQueryParams = buildQueryParams(filters);
      // endpoint += `${filterQueryParams}`;

      // const { startDate, endDate } = dateFilter;

      // if (startDate !== null) {
      //   endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      // }

      // if (endDate !== null) {
      //   endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      // }
      // endpoint += `&sort_by_${sorting.order}=${encodeURIComponent(sorting.column)}`;

      try {
        console.log(endpoint);
        const response = await axios.get<{
          count: number;
          results: OrderType[];
        }>(endpoint, config);
        console.log(response);
        setOrders(response.data.results);
        console.log(orders);
        setOrdersCount(response.data.count);
      } catch (error) {
        console.log(error);
        setOrders([]);
      }
      setLoadingOrders(false);
    };

    getAndSetOrders();
  }, [
    taskData.workflow,
    taskData.customer,
    taskData.merchant,
    taskData.merchant_location,
    taskData.customer_location,
  ]);

  const handleFetchCustomers = async (
    query: string
  ): Promise<CustomerType[]> => {
    if (!taskData.merchant) return []
    const endpoint = `${API_ENDPOINT}/api/v1/customers/?page_size=10&name_or_phone=${encodeURIComponent(query)}&merchant=${taskData.merchant.id}`;

    try {
      const response = await axios.get<{
        count: number;
        results: CustomerType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const handleFetchMerchants = async (
    query: string
  ): Promise<MerchantType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/merchants/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: MerchantType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const handleFetchPickupLocations = async (query: string) => {
    if (taskData.merchant != null) {
      const endpoint = `${API_ENDPOINT}/api/v1/pickups/?page_size=10&merchant=${taskData.merchant.id}&name__contains=${encodeURIComponent(query)}`;

      try {
        const response = await axios.get<{
          count: number;
          results: ShopLocationType[];
        }>(endpoint, config);

        console.log(response);

        return response.data.results;
      } catch (error) {
        console.log(error);
      }
    }
    return [];
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (taskData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving pickup request...');

      const requestData: any = {
        workflow: taskData.workflow,
        orders: Array.from(selectedOrders.values()),
        customer: taskData.customer,
        customer_location: taskData.customer_location,
        merchant_location: taskData.merchant_location,
        merchant: taskData.merchant,
        driver: null,
      };

      try {
        const response = await axios.post(
          `${API_ENDPOINT}/api/v1/pickup-tasks/`,
          requestData,
          config
        );

        setResponse(response);
        onSuccess(response.data, response.data?.was_created);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage(
            'Failed to save pickup request: ' + e.response?.statusText
          );
        } else {
          setMessage('Failed to save pickup request');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        New Pickup Request
      </Typography>
      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {taskData && (
            <>
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <FormLabel id="demo-radio-buttons-group-label">
                    Workflow
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue={taskData.workflow}
                    row
                    name="radio-buttons-group"
                    onChange={(e) => {
                      setTaskData({
                        ...taskData,
                        workflow: e.target.value as Workflow,
                      });
                    }}
                  >
                    <FormControlLabel
                      value={Workflow.STANDARD}
                      control={<Radio />}
                      label="Standard"
                    />
                    <FormControlLabel
                      value={Workflow.RETURN}
                      control={<Radio />}
                      label="Return"
                    />
                  </RadioGroup>
                </FormControl>
                <br />
                <br />
                {taskData.workflow === Workflow.RETURN ? (
                  <>
                    <AutocompleteWithThrottle
                      label={'Merchant'}
                      key={'merchant'}
                      fetch={handleFetchMerchants}
                      value={taskData.merchant}
                      onClickAdd={() => setCreatingNewMerchantOpen(true)}
                      placeholder="Search by name or phone number"
                      getOptionLabel={(option: MerchantType) =>
                        option.name + ' ' + option.phone_number
                      }
                      onChange={(e: any, value: MerchantType | null) => {
                        setTaskData({
                          ...taskData,
                          merchant: value,
                          merchant_location: null,
                        });
                      }}
                    />
                    <br />

                    <AutocompleteWithThrottle
                      label={'Customer'}
                      key={'customer'}
                      fetch={handleFetchCustomers}
                      value={taskData.customer}
                      placeholder="Search by name or phone number"
                      getOptionLabel={showCustomerAutocompleteOption}
                      onChange={(e: any, value: CustomerType | null) => {
                        setTaskData({ ...taskData, customer: value });
                      }}
                    />
                  </>
                ) : (
                  <>
                    <AutocompleteWithThrottle
                      label={'Merchant'}
                      onClickAdd={() => setCreatingNewMerchantOpen(true)}
                      fetch={handleFetchMerchants}
                      fetchAllOnLoad
                      value={taskData.merchant}
                      placeholder="Search by name or phone number"
                      getOptionLabel={(option: MerchantType) =>
                        option.name + ' ' + option.phone_number
                      }
                      onChange={(e: any, value: MerchantType | null) => {
                        setTaskData({
                          ...taskData,
                          merchant: value,
                          merchant_location: null,
                        });
                      }}
                    />

                    <br />

                    <AutocompleteWithThrottle
                      label={'Pickup Location'}
                      key={taskData.merchant?.id}
                      fetchAllOnLoad
                      value={taskData.merchant_location}
                      onChange={(e, value) =>
                        setTaskData({ ...taskData, merchant_location: value })
                      }
                      fetch={handleFetchPickupLocations}
                      onClickAdd={() => {
                        if (taskData.merchant == null)
                          alert('Please select merchant first');
                        else setCreatePickupLocation(true);
                      }}
                      getOptionLabel={function (
                        option: ShopLocationType
                      ): string {
                        return `${option.name}: ${displayLocation(option)}`;
                      }}
                    />
                  </>
                )}
                <br />
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Total Pay on Pickup:{' '}
                  {displayMoney(
                    add(
                      compute_total_pop(Array.from(selectedOrders.values())),
                      taskData.pop
                    )
                  )}
                </Typography>
                <Chip
                  icon={<ChecklistIcon />}
                  color="primary"
                  label={`Selected ${totalSelected}/${ordersCount}`}
                  variant={
                    totalSelected === ordersCount && totalSelected > 0
                      ? 'filled'
                      : 'outlined'
                  }
                />
                <br /> <br />
                <GenericTable
                  sourceView="create-pickup-task-form"
                  items={orders}
                  selectedItems={selectedOrders}
                  updateSelectedItems={updateSelectedOrders}
                  isLoading={loadingOrders}
                  fullWidth
                  selectionEnabled
                  columns={[
                    {
                      id: 'order_id',
                      header: 'Order ID',
                      display_body: (order) => <>{order.order_id}</>,
                    },
                    {
                      id: 'packages',
                      header: '# Packages',
                      display_body: (order) => <>{order.number_of_packages}</>,
                    },
                    {
                      id: 'merchant',
                      header: 'Merchant',
                      display_body: (order) => (
                        <>
                          {order.merchant?.name}
                          <br /> {displayLocation(order.merchant_location)}
                        </>
                      ),
                    },
                    {
                      id: 'customer',
                      header: 'Customer',
                      display_body: (order) => (
                        <>
                          {order.customer?.name}
                          <br /> {displayLocation(order.customer_location)}
                        </>
                      ),
                    },
                    {
                      id: 'pop',
                      header: 'Pay on Pickup',
                      display_body: (order) => <>{displayMoney(order.pop)}</>,
                    },
                    {
                      id: 'workflow',
                      header: 'Workflow',
                      display_body: (order) => (
                        <>{showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason)}</>
                      ),
                    },
                    {
                      id: 'delivery state',
                      header: 'Status',
                      display_body: (order) =>
                        showDeliveryState(order.delivery_state),
                    },
                  ]}
                  getItemUID={(order) => order.id}
                />
                <TablePagination
                  component="div"
                  onPageChange={handlePageChange}
                  page={controller.page}
                  count={ordersCount}
                  rowsPerPage={controller.rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                  labelRowsPerPage=""
                  style={{ overflow: 'visible' }}
                />
                <br />
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    variant="contained"
                    type={'submit'}
                  >
                    Save
                  </Button>
                </div>
              </form>

              <CustomDialog
                fullWidth
                open={createPickupLocation}
                onClose={() => setCreatePickupLocation(false)}
                fullScreen={(isMobile && !isTablet)}
              >
                <div
                  style={{
                    textAlign: 'right',
                    width: '100%',
                  }}
                >
                  <IconButton onClick={() => setCreatePickupLocation(false)}>
                    <ClearIcon color="primary" fontSize="large" />
                  </IconButton>
                </div>

                <DialogContent>
                  <CreatePickupLocationForm
                    merchant={taskData?.merchant ?? emptyMerchant}
                    successCallback={() => {
                      /**nothing to do */
                    }}
                  />
                </DialogContent>
              </CustomDialog>

              <CustomDialog
                fullWidth
                open={createLocation}
                onClose={() => setCreateLocation(false)}
                fullScreen={(isMobile && !isTablet)}
              >
                <div
                  style={{
                    textAlign: 'right',
                    width: '100%',
                  }}
                >
                  <IconButton onClick={() => setCreateLocation(false)}>
                    <ClearIcon color="primary" fontSize="large" />
                  </IconButton>
                </div>

                <DialogContent>
                  {taskData?.customer ? (
                    <CreateLocationForm
                      customer={taskData?.customer}
                      successCallback={() => {
                        /* nothing */
                      }}
                    />
                  ) : (
                    <Alert severity="error">
                      Cannot create location for unknown customer.
                    </Alert>
                  )}
                </DialogContent>
              </CustomDialog>

              <CustomDialog
                fullWidth
                open={creatingNewMerchantOpen}
                onClose={() => setCreatingNewMerchantOpen(false)}
                fullScreen={(isMobile && !isTablet)}
              >
                <div
                  style={{
                    textAlign: 'right',
                    width: '100%',
                  }}
                >
                  <IconButton onClick={() => setCreatingNewMerchantOpen(false)}>
                    <ClearIcon color="primary" fontSize="large" />
                  </IconButton>
                </div>

                <DialogContent>
                  <CreateMerchantForm
                    successCallback={(m) =>
                      setTaskData({ ...taskData, merchant: m })
                    }
                  />
                </DialogContent>
              </CustomDialog>
            </>
          )}
        </>
      )}
    </>
  );
}

export default CreatePickupRequest;
